import { useCallback, useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';
import type { IFloatingOptions } from './IFloatingOptions';
import type { IFloatingPosition } from './IFloatingPosition';
import { pushEscapeLayer } from './escapeLayers';
import { floatingPlacement } from './floatingPlacement';

const same = (a: IFloatingPosition | null, b: IFloatingPosition): boolean =>
  !!a && a.left === b.left && a.top === b.top && a.bottom === b.bottom && a.minWidth === b.minWidth;

/**
 * Плаваючий список біля поля (UIUX/controls/picker, lookup): position fixed під полем або вгору біля низу вікна; закривається
 * кліком поза полем і списком, прокруткою поза списком (якщо поле зсунулося) та Esc. Esc закриває лише верхній відкритий шар
 * (стек escapeLayers): список у діалозі чи панелі не закриває діалог або панель — patterns/keyboard.
 */
export function useFloating(
  open: boolean,
  anchor: RefObject<HTMLElement | null>,
  panel: RefObject<HTMLElement | null>,
  onClose: () => void,
  { maxHeight = 300, estimate = 300, minWidth = 0, gap = 4, matchWidth = true }: IFloatingOptions = {},
): IFloatingPosition | null {
  const [pos, setPos] = useState<IFloatingPosition | null>(null);
  const close = useRef(onClose);
  const placed = useRef({ top: 0, left: 0 });
  useEffect(() => { close.current = onClose; }, [onClose]);
  const place = useCallback(() => {
    const a = anchor.current;
    if (!a) return;
    const r = a.getBoundingClientRect();
    placed.current = { top: r.top, left: r.left };
    const p = panel.current;
    const size = { width: p?.offsetWidth ?? 0, height: Math.min(maxHeight, p?.offsetHeight || estimate) };
    const next = floatingPlacement(r, size, { width: window.innerWidth, height: window.innerHeight }, gap, minWidth, matchWidth);
    setPos((prev) => (same(prev, next) ? prev : next));
  }, [anchor, panel, maxHeight, estimate, minWidth, gap, matchWidth]);
  useLayoutEffect(() => { if (open) place(); else setPos(null); }, [open, place]);
  // Уточнення після малювання списку: справжня висота й ширина замість оцінки.
  useLayoutEffect(() => { if (open && pos) place(); });
  useEffect(() => {
    if (!open) return undefined;
    const inside = (t: EventTarget | null) => t instanceof Node && (!!anchor.current?.contains(t) || !!panel.current?.contains(t));
    const onDown = (e: MouseEvent) => { if (!inside(e.target)) close.current(); };
    // Прокрутка поза списком закриває його, лише якщо поле справді зсунулося (пізні події прокрутки після фокуса — ні).
    const onScroll = (e: Event) => {
      const r = anchor.current?.getBoundingClientRect();
      if (!inside(e.target) && (!r || Math.abs(r.top - placed.current.top) > 1 || Math.abs(r.left - placed.current.left) > 1)) close.current();
    };
    const pop = pushEscapeLayer(() => close.current());
    document.addEventListener('mousedown', onDown, true);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', place);
    return () => {
      pop();
      document.removeEventListener('mousedown', onDown, true);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', place);
    };
  }, [open, anchor, panel, place]);
  return pos;
}
